<template>
  <div class="ai-chat-widget" v-if="isAiChatEnabled">
    <!-- Floating Action Button -->
    <div v-if="!isOpen" class="ai-chat-fab" @click="toggleChat">
      <vue-feather type="message-circle" class="fab-icon"></vue-feather>
    </div>

    <!-- Chat Window -->
    <div v-else class="ai-chat-window">
      <div class="chat-header">
        <div class="header-title">
          <vue-feather type="cpu" class="header-icon"></vue-feather>
          <span>AI Assistant</span>
        </div>
        <vue-feather type="x" class="close-icon" @click="toggleChat"></vue-feather>
      </div>

      <div class="chat-messages" ref="messagesContainer">
        <!-- Welcome / empty state -->
        <div v-if="messages.length === 0 && !isLoading" class="empty-state">
          <p class="welcome-main">Hi! I'm the ryd portal AI Assistant.</p>
          <p class="welcome-hint">
            This assistant is designed to make your life easier — helping you find the right
            content, pages, and answers as quickly as possible. It's still being improved, so please
            help ryd enhance the AI knowledge base by providing your feedback on answers. We
            apologize for any incorrect or incomplete responses.
          </p>
          <div class="suggestions">
            <button
              v-for="chip in suggestionChips"
              :key="chip"
              class="suggestion-chip"
              @click="askTemplate(chip)"
            >
              {{ chip }}
            </button>
          </div>
        </div>

        <!-- History notice -->
        <div v-if="showHistoryNotice && messages.length > 0" class="history-notice">
          <vue-feather type="info" class="history-notice-icon"></vue-feather>
          <span>Showing chat history from the last 7 days (max 50 messages).</span>
        </div>

        <!-- Messages -->
        <div v-for="(msg, index) in messages" :key="index" :class="['message', msg.role]">
          <div class="message-content">
            <span v-if="msg.text" class="message-text" v-html="renderMarkdown(msg.text)"></span>
            <div v-else-if="isLoading && index === messages.length - 1" class="typing-indicator">
              <span class="dot"></span><span class="dot"></span><span class="dot"></span>
            </div>
          </div>

          <!-- Feedback row for completed AI messages -->
          <div v-if="msg.role === 'ai' && msg.text && !isLoading" class="feedback-row">
            <template v-if="!msg.feedbackGiven">
              <span class="feedback-label">Was this helpful?</span>
              <button class="feedback-btn bad" @click="openFeedback(index, 'bad')" title="Bad">
                👎
              </button>
              <button class="feedback-btn fine" @click="openFeedback(index, 'fine')" title="Fine">
                😐
              </button>
              <button class="feedback-btn good" @click="openFeedback(index, 'good')" title="Good">
                👍
              </button>
            </template>
            <template v-else>
              <span class="feedback-thanks">
                {{
                  msg.feedbackRating === 'good' ? '👍' : msg.feedbackRating === 'fine' ? '😐' : '👎'
                }}
                Thank you for your feedback!
              </span>
            </template>
          </div>

          <!-- Feedback comment input -->
          <div
            v-if="feedbackActiveIndex === index && !msg.feedbackGiven"
            class="feedback-comment-area"
          >
            <input
              v-model="feedbackComment"
              type="text"
              placeholder="Any additional comments? (optional)"
              @keyup.enter="submitFeedback(index)"
              class="feedback-input"
            />
            <button class="feedback-submit-btn" @click="submitFeedback(index)">Submit</button>
            <button class="feedback-skip-btn" @click="submitFeedback(index)">Skip</button>
          </div>
        </div>
      </div>

      <!-- Input area -->
      <div class="chat-input-wrapper">
        <div v-if="showTemplates" class="templates-dropdown">
          <div
            v-for="tpl in questionTemplates"
            :key="tpl"
            class="template-option"
            @click="fillTemplate(tpl)"
          >
            {{ tpl }}
          </div>
        </div>
        <div class="chat-input-area">
          <button
            class="template-btn"
            @click="showTemplates = !showTemplates"
            title="Insert Template"
          >
            <vue-feather type="zap" class="template-icon"></vue-feather>
          </button>
          <input
            ref="chatInput"
            v-model="currentQuestion"
            type="text"
            placeholder="Ask a question..."
            @keyup.enter="askQuestion"
            :disabled="isLoading"
          />
          <button
            class="send-btn"
            @click="askQuestion"
            :disabled="isLoading || !currentQuestion.trim()"
          >
            <vue-feather type="send" class="send-icon"></vue-feather>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { aiChatService, renderMarkdown } from '@/services/ai-chat';

export default {
  name: 'AiChatWidget',
  data() {
    return {
      isOpen: false,
      currentQuestion: '',
      messages: [],
      isLoading: false,
      showTemplates: false,
      showHistoryNotice: false,
      feedbackActiveIndex: null,
      feedbackRating: null,
      feedbackComment: '',
    };
  },
  computed: {
    isAiChatEnabled() {
      const userAddDataRaw = localStorage.getItem('userAddData');
      if (userAddDataRaw) {
        try {
          const data = JSON.parse(userAddDataRaw);
          const types = data.partnerTypes;
          // Disable for users who ONLY have FLEET_PARTNER
          if (Array.isArray(types) && types.length === 1 && types[0] === 'FLEET_PARTNER') {
            return false;
          }
        } catch (e) {
          /* ignore */
        }
      }
      return true;
    },
    userGroup() {
      const userAddDataRaw = localStorage.getItem('userAddData');
      if (userAddDataRaw) {
        try {
          return JSON.parse(userAddDataRaw).userGroup;
        } catch (e) {
          /* ignore */
        }
      }
      return null;
    },
    userContext() {
      try {
        const raw = localStorage.getItem('userAddData');
        if (raw) {
          const data = JSON.parse(raw);
          return {
            partnerTypes: data.partnerTypes,
            roles: data.roles,
            userGroup: data.userGroup,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            timezone: data.timezone,
          };
        }
      } catch (e) {
        console.warn('Failed to parse userAddData from localStorage', e);
      }
      return {};
    },
    questionTemplates() {
      const templates = [];
      if (this.userGroup === 'ryd' || this.userGroup === 'RYD_INTERNAL') {
        templates.push('Go to User Details of ');
        templates.push("What are the user's GPS location data when this order was created: ");
      }
      templates.push('Provide me the details of the order: ');
      templates.push('What does the action code mean: ');
      return templates;
    },
    suggestionChips() {
      const chips = ['What is autoAcquire?'];
      if (this.userGroup === 'ryd' || this.userGroup === 'RYD_INTERNAL') {
        chips.push('How do I look up an order by UUID?');
        chips.push('How do I block a customer account?');
      } else {
        chips.push('What API versions are available?');
        chips.push('How does the order lifecycle work?');
      }
      return chips;
    },
  },
  methods: {
    renderMarkdown,

    async toggleChat() {
      this.isOpen = !this.isOpen;
      if (this.isOpen && this.messages.length === 0) {
        this.isLoading = true;
        try {
          const history = await aiChatService.fetchHistory();
          if (history.length > 0) {
            // Because $set doesn't work simply on array replacement, we push elements or replace array entirely
            this.messages = [...history];
            this.showHistoryNotice = true;
            this.scrollToBottom();
          }
        } catch (e) {
          console.error('Failed to load chat history', e);
        } finally {
          this.isLoading = false;
        }
        this.$nextTick(() => {
          const input = this.$el.querySelector('.chat-input-area input');
          if (input) input.focus();
        });
      }
    },

    askTemplate(question) {
      this.currentQuestion = question;
      this.askQuestion();
    },

    fillTemplate(template) {
      this.currentQuestion = template;
      this.showTemplates = false;
      this.$nextTick(() => {
        if (this.$refs.chatInput) this.$refs.chatInput.focus();
      });
    },

    openFeedback(index, rating) {
      this.feedbackActiveIndex = index;
      this.feedbackRating = rating;
      this.feedbackComment = '';
    },

    async submitFeedback(index) {
      const msg = this.messages[index];
      // Find the preceding user question
      let questionText = '';
      for (let i = index - 1; i >= 0; i--) {
        if (this.messages[i].role === 'user') {
          questionText = this.messages[i].text;
          break;
        }
      }

      // Snapshot the rating/comment before resetting state below.
      const rating = this.feedbackRating;
      const comment = this.feedbackComment.trim();

      // Optimistic UI: reflect the submission immediately. Vue 3 reactivity
      // covers direct property assignment, so no $set helper is needed.
      this.messages[index].feedbackGiven = true;
      this.messages[index].feedbackRating = rating;
      this.feedbackActiveIndex = null;
      this.feedbackRating = null;
      this.feedbackComment = '';

      // Fire the network call after the UI has already updated. Failures are
      // logged but do not reverse the optimistic state — the user has moved on.
      try {
        await aiChatService.submitFeedback({
          messageId: msg.id || null,
          question: questionText,
          answer: msg.text,
          rating,
          comment,
        });
      } catch (e) {
        console.error('Failed to submit feedback', e);
      }
    },

    async askQuestion() {
      const q = this.currentQuestion.trim();
      if (!q || this.isLoading) return;

      this.showHistoryNotice = false;
      this.messages.push({ role: 'user', text: q, feedbackGiven: false, feedbackRating: null });
      this.currentQuestion = '';
      this.isLoading = true;

      this.messages.push({ role: 'ai', text: '', feedbackGiven: false, feedbackRating: null });
      const aiMsgIndex = this.messages.length - 1;
      const conversationContext = this.messages
        .slice(0, -2)
        .filter((message) => message.text)
        .slice(-6)
        .map((message) => ({ role: message.role, text: message.text }));
      this.scrollToBottom();

      try {
        await aiChatService.askQuestion(
          q,
          this.userContext,
          conversationContext,
          (chunk) => {
            this.messages[aiMsgIndex].text += chunk;
            this.scrollToBottom();
          },
          (errorMsg) => {
            this.messages[aiMsgIndex].text += `\n${errorMsg}`;
            this.scrollToBottom();
          },
        );
      } catch (err) {
        console.error(err);
        this.messages[aiMsgIndex].text =
          err?.name === 'UsageLimitError'
            ? `${err.message} Your usage is shown on the Usage & Limits page.`
            : "Sorry, I couldn't reach the AI service right now. " + err.message;
      } finally {
        this.isLoading = false;
        this.scrollToBottom();
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) container.scrollTop = container.scrollHeight;
      });
    },
  },
};
</script>

<style src="./AiChatWidget.css"></style>
